<template>
    <div
        v-if="summary || reviews.length"
        class="customer-reviews-ssr"
        :class="{ 'customer-reviews-ssr--visually-hidden': isVisuallyHidden }"
    >
        <!-- Feefo Attribution -->
        <div class="feefo-attribution">
            <img
                src="https://api.feefo.com/api/logo?merchantidentifier=skypark-secure"
                alt="Feefo logo"
                width="100"
                height="50"
                loading="lazy"
            />
            <p>Reviews collected and hosted by Feefo, an independent reviews platform.</p>
        </div>

        <!-- Summary (service or product) -->
        <div v-if="summary" class="customer-reviews-summary">
            <p>
                <span>{{ summary.rating }}</span> /
                <span>{{ summary.maxRating }}</span>
                <span v-if="summary.reviewsCount">
                    (<span>{{ summary.reviewsCount }}</span> reviews)
                </span>
            </p>
        </div>

        <!-- Individual reviews -->
        <ul v-if="reviews.length" class="customer-reviews-list">
            <li v-for="review in reviews" :key="review.id" class="customer-reviews-item">
                <p class="review-rating">Rating: {{ review.rating }} / 5</p>

                <p v-if="review.title" class="review-title">
                    {{ review.title }}
                </p>

                <p class="review-body">
                    {{ review.comment }}
                </p>

                <p class="review-meta">
                    <span v-if="review.author" class="review-author">{{ review.author }}</span>
                    <span v-if="review.author && review.date"> · </span>
                    <time v-if="review.date" :datetime="review.date" class="review-date">
                        {{ formatDate(review.date) }}
                    </time>
                </p>
            </li>
        </ul>

        <!-- Feefo link for transparency -->
        <p class="feefo-link">
            <a href="https://www.feefo.com/en-GB/reviews/skypark-secure" target="_blank" rel="noopener noreferrer">
                View all reviews on Feefo
            </a>
        </p>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
    useFeefoProductReviews,
    useFeefoProductSummary,
    useFeefoServiceReviews,
    useFeefoServiceSummary,
} from '@nuxt-monorepo/ui/composables/useFeefoReviews';
import type { IFeefoServiceReview, IFeefoSummary } from '@nuxt-monorepo/ui/types/seo/FeefoReviews';

const props = defineProps<{
    limit?: number;
    visuallyHidden?: boolean;
    productSku?: string;
}>();

const limit = props.limit ?? 5;

/**
 * Reviews
 */
const reviewsState = props.productSku ? useFeefoProductReviews(props.productSku, limit) : useFeefoServiceReviews(limit);

const reviews = computed<IFeefoServiceReview[]>(() => reviewsState.reviews.value ?? []);

/**
 * Summary
 */
const summaryState = props.productSku ? useFeefoProductSummary(props.productSku) : useFeefoServiceSummary();

const summary = computed<IFeefoSummary | null>(() => summaryState.summary.value ?? null);

const isVisuallyHidden = computed(() => props.visuallyHidden === true);

const formatDate = (raw?: string) => {
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;

    return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};
</script>

<style scoped>
.customer-reviews-ssr--visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
